/* eslint-disable factorial/no-design-system-long-imports */
import React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { EnvironmentContext } from 'config/environment'

import Select from 'modules/core/components/Inputs/Select'
import InputText from 'modules/core/components/Inputs/Text'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { useValidateVatNumber } from 'modules/billing/hooks/useValidateVatNumber'

import { ChargebeeSubscriptionForm } from '../../FullScreenStep/form'
import { getVATAbbreviationByCountry, mxTaxSystemOptions, statesOptions } from '../helpers'

type Props = {
  form: ChargebeeSubscriptionForm
  country: string
}

const Body = ({ form, country }: Props) => {
  const { captureExplicitSentryErrors } = React.useContext(EnvironmentContext)
  const { isLoading, validate } = useValidateVatNumber(form, captureExplicitSentryErrors)
  const fetchingProps = {
    loading: isLoading,
    readonly: isLoading,
    placeholder: isLoading ? '...' : '',
  }

  return (
    <Box paddingTop="s16" gap="s16">
      {form.visibleFields.includes('mx_tax_system') && (
        <Select
          none={i18n.t('pricing_plans.billing_information.form.mx_tax_system_placeholder')}
          label={i18n.t('pricing_plans.billing_information.form.mx_tax_system')}
          field={form.get('mx_tax_system')}
          options={mxTaxSystemOptions}
          mandatory
        />
      )}
      {form.visibleFields.includes('vat_number') && (
        <InputText
          label={getVATAbbreviationByCountry(country)}
          field={form.get('vat_number')}
          mandatory
          onChange={(e) => {
            const inputValue = e.value.slice(0, 50)
            form.setErrors({ vat_number: {} })
            form.setValues({
              vat_number: inputValue,
            })
          }}
          onBlur={validate}
        />
      )}
      {form.visibleFields.includes('company') && (
        <InputText
          label={i18n.t('pricing_plans.billing_information.form.company')}
          field={form.get('company')}
          mandatory
          {...fetchingProps}
        />
      )}
      {form.visibleFields.includes('address_line') && (
        <InputText
          label={i18n.t('pricing_plans.billing_information.form.address_line')}
          field={form.get('address_line')}
          mandatory
          {...fetchingProps}
        />
      )}
      {form.visibleFields.includes('city') && (
        <InputText
          label={i18n.t('pricing_plans.billing_information.form.city')}
          field={form.get('city')}
          mandatory
        />
      )}
      <Box gap="s12" flexDirection="row">
        {form.visibleFields.includes('zip') && (
          <InputText
            label={
              form.get('country').value === 'us'
                ? i18n.t('pricing_plans.billing_information.form.zip')
                : i18n.t('pricing_plans.billing_information.form.post_code')
            }
            field={form.get('zip')}
            mandatory={
              !!form.get('country').value && ['es', 'mx', 'br'].includes(form.get('country').value)
            }
            {...fetchingProps}
          />
        )}
        {['us', 'mx'].includes(form.get('country').value) ? (
          <Select
            none={i18n.t('pricing_plans.billing_information.form.state_placeholder')}
            label={i18n.t('pricing_plans.billing_information.form.state')}
            field={form.get('state')}
            options={statesOptions(form.get('country').value)}
            mandatory
          />
        ) : (
          <InputText
            label={i18n.t('pricing_plans.billing_information.form.state')}
            field={form.get('state')}
            {...fetchingProps}
          />
        )}
      </Box>

      {form.visibleFields.includes('email') && (
        <InputText
          label={i18n.t('pricing_plans.billing_information.form.email')}
          field={form.get('email')}
          mandatory
          onChange={(e) => {
            form.setErrors({ email: {} })
            form.setValues({ email: e.value })
          }}
          onBlur={() => {
            if (form.get('email').value !== '' && !form.isValidEmail) {
              track(trackingEvents.billing.upgradePlanError, {
                error_message: 'Invalid email',
                value: form.get('email').value,
              })
              form.setErrors({
                email: [i18n.t('pricing_plans.billing_information.form.validation.invalid_email')],
              })
            }
          }}
        />
      )}
    </Box>
  )
}

export default Body
